import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button } from 'react-bootstrap'
import { isLoggedIn } from '../../services/authenticationService'
import { formatCurrency } from '../../helpers'
import LogoutButton from 'application/components/logoutButton'
import ConsultantLogoutButton from 'application/components/pages/consultant/consultantLogoutButton'
import ContactUsDialog from '../dialog/contactUsDialog'
import { Icon as RemServIcon } from 'RemServ/icons'
import { Image as MaxxiaImage } from 'Maxxia/images'
import { useAccordionToggle } from 'react-bootstrap/AccordionToggle'
import useComponentVisible from 'application/helpers/useComponentVisible'
import * as E from './mainNavElements'
import { FlowGraph, PrimaryButton } from '@mmsg/louie'
import { useWindowSize } from 'application/helpers'
import { NextStepValue } from 'application/utils/type'
import { useSelector, useDispatch } from 'react-redux'
import { fetchNextsteps } from 'application/actions/nextstepsActions'
import { httpVerbConstants } from 'application/constants/actionTypes'
import { getCurrentResolutionBreakPoint } from 'application/utils/utilityFunctions'

const MenuExpander = (props) => {
  const [isActive, setIsActive] = useState(false)
  const decoratedOnClick = useAccordionToggle('0', () => {
    setIsActive(!isActive)
  })

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === 'NumpadEnter') {
      var selectedItem = document.activeElement
      if (selectedItem.id == 'mainnav.contactconsultant') {
        setIsActive(
          document.getElementById('mainnav.menuconsulantname') == null
        )
      }

      if (selectedItem.id == 'mainnav.mydetailsitem') {
        setIsActive(
          document.getElementById('mainnav.menudetailscustomername') == null
        )
      }
    }
  }

  return (
    <div>
      <E.MenuAccordionTitleDiv onClick={decoratedOnClick} eventkey={'0'}>
        <E.MenuAccordionTitleSpan
          isActive={isActive}
          id={props.id}
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          {props.title}
          <E.MenuAccordionArrow
            isActive={isActive}
            src="/images/nav-down-icon.png"
          />
        </E.MenuAccordionTitleSpan>
      </E.MenuAccordionTitleDiv>

      {isActive && (
        <E.MenuAccordionContainer>
          {props.contents
            .filter((item) => item.value || item.showWithNullValue)
            .map((item, i) => (
              <div className="mb-2">
                <E.MenuTitle>{item.heading}</E.MenuTitle>
                <E.MenuValue id={item.id}>{item.value}</E.MenuValue>
              </div>
            ))}
          {props.additionalComponent && (
            <E.MenuAccordionComponentDiv>
              {props.additionalComponent}
            </E.MenuAccordionComponentDiv>
          )}
        </E.MenuAccordionContainer>
      )}
    </div>
  )
}

const MainNav = ({ applicationRoles, customer, consultant, isGov }) => {
  const { isConsultant } = applicationRoles
  const employer = customer.employer || {}
  const customerName = customer.firstName + ' ' + customer.lastName
  const consultantName = consultant.firstName + ' ' + consultant.lastName
  const estimatesLink = isConsultant ? '/consultant/estimates' : '/estimates'
  const homeLink = isConsultant ? '/consultant/home' : '/home'
  const settlementsLink = isConsultant
    ? '/consultant/settlements'
    : '/settlements'
  const userActivityLink = '/consultant/useractivity'
  const [showSkipToContent, setShowSkipToContent] = useState(false)
  const { ref, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false)

  let details = [
    {
      heading: 'Name',
      value: customerName,
      id: 'mainnav.menudetailscustomername',
      key: 1
    },
    {
      heading: 'Employer',
      value: employer.name,
      id: 'mainnav.menudetailsemployer',
      key: 2
    },
    {
      heading: 'Annual Salary',
      value: formatCurrency(employer.grossAnnualSalary),
      id: 'mainnav.menudetailssalary',
      key: 3
    },
    {
      heading: 'Pay cycle',
      value: employer.payFrequency,
      id: 'mainnav.menudetailspaycycle',
      key: 4
    },
  ]

  let contactConsultantDetails = [
    {
      heading: 'Consultant name',
      value: consultantName,
      id: 'mainnav.menuconsulantname',
      key: 5
    },
    {
      heading: 'Email',
      value: consultant.email,
      showWithNullValue: false,
      id: 'mainnav.menuconsulantemail',
      key: 6
    },
    {
      heading: 'Phone number',
      value: consultant.telephone,
      showWithNullValue: false,
      id: 'mainnav.menuconsulantphone',
      key: 7
    },
    {
      heading:
        consultant.firstName +
        ' is your personal consultant, dedicated to guiding you through this process. Want to loop someone into your decision? ' +
        consultant.firstName +
        ' can help bring them up to speed.',
      value: null,
      showWithNullValue: true,
      id: 'mainnav.menuconsulantblahblah',
      key: 8
    },
  ]

  let contactConsultantComponent = isConsultant ? (
    <Button
      className="text-brand font-weight-bold btn btn-primary text-white readonly"
      theme="button"
      size="sm"
      extendInMob
    >
      Request call back
    </Button>
  ) : (
    <ContactUsDialog
      className="text-brand font-weight-bold btn btn-primary text-white"
      theme="button"
      size="sm"
      extendInMob
      launchText="Request call back"
      id="mainnav.contactusdialog"
    />
  )

  const toggleMenuVisibility = () => {
    setIsComponentVisible(!isComponentVisible)
  }
  const resolutionBreakpoint = {
    nonDesktop: getCurrentResolutionBreakPoint(false),
  }
  const [width] = useWindowSize()
  let opportunity = useSelector((state) => state.opportunity)

  let currentFlowGraphState = 0
  if (opportunity.state && opportunity.state.toLowerCase() == 'won') {
    currentFlowGraphState = 5
  }

  let isProposalAccepted =
    applicationRoles &&
    applicationRoles.hasProposal &&
    applicationRoles.hasSubmittedEstimate &&
    applicationRoles.hasProposalAccepted &&
    opportunity &&
    opportunity.state &&
    opportunity.state !== 'Lost'

  const { opportunityId } = useSelector((state) => state.opportunity)
  const currentConsultantEmail = useSelector(
    (state) =>
      state.auth &&
      state.auth.idToken &&
      state.auth.idToken.claims &&
      state.auth.idToken.claims.email
  )
  const consultantUserViewValues =
    process.env.REACT_APP_CONSULTANTCUSTOMERVIEW.toLowerCase()

  let showUserActivityMenu = false
  // if (isConsultant) {
  //   if (consultantUserViewValues == '*') showUserActivityMenu = true
  //   else {
  //     let consultantList = consultantUserViewValues.split(';')
  //     if (consultantList.indexOf(currentConsultantEmail.toLowerCase()) != -1)
  //       showUserActivityMenu = true
  //   }
  // }
  const { accessToken } = useSelector((state) => state.auth)
  const dispatch = useDispatch()
  const [getNext, setGetNext] = useState(false)
  const headers = { accessToken: accessToken }

  useEffect(() => {
    if (opportunityId && isProposalAccepted) {
      dispatch(
        fetchNextsteps(opportunityId, headers, httpVerbConstants.HTTP_GET)
      )
    }
  }, [getNext])

  useEffect(() => {
    const listener = (event) => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        var myquoteMenuItem = document.getElementById('mainnav.myquoteitem')

        setIsComponentVisible(myquoteMenuItem == null)
      }
    }

    var elem = document.getElementById('mainnav.menutitlediv')
    if (elem) elem.addEventListener('keydown', listener)
  }, [])

  useEffect(() => {
    const listener = (event) => {
      if (event.code === 'Escape') setIsComponentVisible(false)
      if (
        event.code === 'Tab' &&
        window.location.href.toLowerCase().indexOf('home') != -1
      ) {
        setShowSkipToContent(true)
      }
    }

    document.addEventListener('keydown', listener)
  }, [])

  if (opportunityId && !getNext && currentFlowGraphState == 0) {
    setGetNext(true)
  }
  let currentStep
  let settlement
  useSelector((state) => (currentStep = state.nextSteps.step))
  useSelector((state) => (settlement = state.opportunity.settlement))

  if (currentFlowGraphState != 5) {
    switch (currentStep) {
      case NextStepValue.ATO: {
        currentFlowGraphState = 2
        break
      }
      case NextStepValue.DEFERRED:
      case NextStepValue.INSURANCE: {
        currentFlowGraphState = 3
        break
      }
      case NextStepValue.NONE: {
        if (applicationRoles.hasProposalAccepted)
          if (
            settlement &&
            settlement.recipients &&
            settlement.recipients.filter((r) => !r.hasSigned) == 0
          )
            currentFlowGraphState = 4
          else currentFlowGraphState = 3

        break
      }
      default: {
        currentFlowGraphState = 0
      }
    }
    if (
      applicationRoles &&
      !applicationRoles.hasProposalAccepted &&
      applicationRoles.hasProposal
    ) {
      currentFlowGraphState = 2
    }
    if (currentFlowGraphState == 0) {
      currentFlowGraphState = 1
    }
  }

  let settlementMenuItemVisible = useSelector(
    (state) =>
      state.opportunity.settlement &&
      state.opportunity.settlement.agreementId &&
      applicationRoles.hasProposalAccepted &&
      currentStep == NextStepValue.NONE
  ) //Either doing settlement or has completed it

  return (
    <>
      {applicationRoles && applicationRoles.hasProposalAccepted && (
        <div id="notification" className="text-center">
          You have accepted a proposal. Contact your consultant if you have any
          more questions
        </div>
      )}
      <E.MainNavContainer>
        <E.MainLogoContainer>
          <Link
            to={homeLink}
            id="mainnav.homelink"
            tabIndex={
              window.location.href.toLowerCase().indexOf('home') != -1 ? -1 : 0
            }
          >
            {global.config.isBrandMX && (
              <MaxxiaImage imageName="LogoNormal" alt="Maxxia DEP Logo" />
            )}
            {global.config.isBrandRS && isGov && (
              <RemServIcon iconType="remservLeaseLogo" size={2.2} />
            )}
            {global.config.isBrandRS && !isGov && (
              <RemServIcon iconType="remservMainLogo" size={2} />
            )}
          </Link>
          <span>
            Visited link, {global.config.brand} DEP site logo button-Pressing
            this button takes you to the home page of {global.config.brand} DEP
            site
          </span>
        </E.MainLogoContainer>
        {width > resolutionBreakpoint.nonDesktop && (
          <>
            {window.location.href.toLowerCase().indexOf('home') != -1 &&
              showSkipToContent && (
                <PrimaryButton
                  invertColor
                  variant="invertColor"
                  onClick={() => document.getElementById('maincontent').focus()}
                >
                  Skip to main content
                </PrimaryButton>
              )}
            <E.MainFlowBlock>
              <FlowGraph
                brand={global.config.brand.toLowerCase()}
                activeTill={currentFlowGraphState + 1}
                items={[
                  'Get Quote',
                  'Finance Approval',
                  'Order Car',
                  'Paperwork',
                  'Drive Time',
                ]}
              ></FlowGraph>
            </E.MainFlowBlock>
          </>
        )}
        {(isConsultant || isLoggedIn()) && (
          <div>
            <E.MenuTitleDiv isShowing={isComponentVisible} ref={ref}>
              <div
                style={{
                  float: 'right',
                  display: 'flex',
                  alignItems: 'right',
                  cursor: 'pointer',
                  width: '120px',
                }}
                id="mainnav.menutitlediv"
                tabIndex={0}
                onClick={toggleMenuVisibility}
              >
                <E.MenuWordDiv>
                  {!isComponentVisible ? 'Menu' : 'Close'}
                </E.MenuWordDiv>
                <E.MenuHamburgerIcon
                  iconType={
                    !isComponentVisible ? 'Hamburger' : 'HamburgerClose'
                  }
                  primary
                  id="mainnav.hamburgerimage"
                />
              </div>
              {isComponentVisible && (
                <E.MenuContainingDiv>
                  {global.config.isBrandMX && (
                    <E.MenuDoubleXImage imageName="LogoSmall" alt="" />
                  )}
                  {global.config.isBrandRS && (
                    <E.MenuIconDiv isGov={isGov}>
                      {isGov ? (
                        <RemServIcon iconType="remservLeaseLogo" size={1.6} />
                      ) : (
                        <RemServIcon iconType="remservMainLogo" size={1} />
                      )}
                    </E.MenuIconDiv>
                  )}
                  {/* {isConsultant && (
                    <div>
                      <div className="dropdown-divider mb-3" />
                      <E.MenuLinkTitle
                        to={userActivityLink}
                        id="mainnav.useractivityitem"
                      >
                        User activity
                      </E.MenuLinkTitle>
                    </div>
                  )} */}
                  <div className="dropdown-divider mb-3" />
                  <E.MenuLinkTitle to={estimatesLink} id="mainnav.myquoteitem">
                    My quote
                  </E.MenuLinkTitle>
                  <div className="dropdown-divider mb-3" />
                  <MenuExpander
                    title="My details"
                    contents={details}
                    id="mainnav.mydetailsitem"
                  />{' '}
                  <div className="dropdown-divider mb-3" />
                  {settlementMenuItemVisible && (
                    <div>
                      <E.MenuLinkTitle
                        to={settlementsLink}
                        id="mainnav.settlements"
                      >
                        Paperwork
                      </E.MenuLinkTitle>
                      <div className="dropdown-divider mb-3" />
                    </div>
                  )}
                  <MenuExpander
                    title="Contact consultant"
                    contents={contactConsultantDetails}
                    additionalComponent={contactConsultantComponent}
                    id="mainnav.contactconsultant"
                  />
                  <div className="dropdown-divider mb-3" />
                  <div className="mb-3" style={{marginLeft:'13px'}}>
                    {isConsultant ? (
                      <ConsultantLogoutButton id="mainnav.consultantlogout" />
                    ) : (
                      <LogoutButton id="mainnav.userlogout" />
                    )}
                  </div>
                </E.MenuContainingDiv>
              )}
            </E.MenuTitleDiv>
          </div>
        )}
      </E.MainNavContainer>
      {width < resolutionBreakpoint.nonDesktop && (
        <E.MainFlowBlockMobile>
          <FlowGraph
            brand={global.config.brand.toLowerCase()}
            activeTill={currentFlowGraphState + 1}
            items={[
              'Get Quote',
              'Finance Approval',
              'Order Car',
              'Paperwork',
              'Drive Time',
            ]}
          />
        </E.MainFlowBlockMobile>
      )}
    </>
  )
}
export default MainNav
